/**
 * COMPONENT: SchoolAutocomplete
 * Autocomplete d'écoles pour les formulaires publics (item #3 retours client).
 *
 * - Appelle le RPC `search_schools` (SECURITY DEFINER, ouvert à anon) pour
 *   récupérer les suggestions à partir de 2 caractères.
 * - Affiche les suggestions sous le champ avec sélection clavier (↑ ↓ ↵ Esc).
 * - Stocke le NOM de l'école dans la valeur du formulaire (string), pour rester
 *   compatible avec le mapping `contact_field_map = 'school'` consommé par le
 *   trigger SQL `create_contact_from_submission()` qui se charge du lookup +
 *   création + lien `contacts.school_id`.
 *
 * Pas de portal : l'autocomplete s'affiche en flux pour rester compatible
 * avec le mode iframe legacy (cf. RadixSelect.disablePortal).
 */

import { Loader2 } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { publicSupabase } from '../../lib/supabase';
import { cn } from '../../lib/utils';

interface SchoolSuggestion {
  id: string;
  name: string;
  postal_code: string | null;
  city: string | null;
}

export interface SchoolSuggestionSelected {
  id: string;
  name: string;
  city: string | null;
  postal_code: string | null;
}

interface SchoolAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  /** Sélection d'une école EXISTANTE (avec sa ville) — retour 4 du BED (00252). */
  onSelect?: (school: SchoolSuggestionSelected) => void;
  placeholder?: string;
  id?: string;
  hasError?: boolean;
  inputClassName?: string;
}

const DEBOUNCE_MS = 200;
const MIN_QUERY_LENGTH = 2;

export function SchoolAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = 'Tapez le nom de votre école…',
  id,
  hasError = false,
  inputClassName,
}: SchoolAutocompleteProps) {
  const reactId = useId();
  const inputId = id || reactId;
  const containerRef = useRef<HTMLDivElement>(null);

  const [suggestions, setSuggestions] = useState<SchoolSuggestion[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Debounce + recherche RPC
  useEffect(() => {
    const trimmed = value.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const handle = window.setTimeout(async () => {
      const { data, error } = await publicSupabase.rpc('search_schools', {
        query: trimmed,
        max_results: 8,
      });

      if (error) {
        console.warn('[SchoolAutocomplete] Erreur recherche écoles:', error);
        setSuggestions([]);
      } else {
        setSuggestions((data as SchoolSuggestion[]) || []);
      }
      setIsLoading(false);
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(handle);
      setIsLoading(false);
    };
  }, [value]);

  // Fermer le dropdown au clic en dehors
  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleSelect = (school: SchoolSuggestion) => {
    onChange(school.name);
    onSelect?.(school);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || suggestions.length === 0) {
      if (event.key === 'ArrowDown' && suggestions.length > 0) {
        setIsOpen(true);
        setHighlightedIndex(0);
        event.preventDefault();
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setHighlightedIndex((prev) => (prev + 1) % suggestions.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        setHighlightedIndex((prev) => (prev <= 0 ? suggestions.length - 1 : prev - 1));
        break;
      case 'Enter':
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          event.preventDefault();
          handleSelect(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  const showDropdown =
    isOpen && (isLoading || suggestions.length > 0 || value.trim().length >= MIN_QUERY_LENGTH);

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        id={inputId}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
          setIsOpen(true);
          setHighlightedIndex(-1);
        }}
        onFocus={() => {
          if (value.trim().length >= MIN_QUERY_LENGTH) {
            setIsOpen(true);
          }
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={showDropdown}
        aria-controls={`${inputId}-listbox`}
        className={cn(
          'h-9 w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900',
          hasError ? 'border-red-300' : 'border-zinc-200',
          inputClassName
        )}
      />
      {isLoading && (
        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 size-4 text-zinc-400 animate-spin" />
      )}

      {showDropdown && (
        <ul
          id={`${inputId}-listbox`}
          role="listbox"
          className="absolute z-50 left-0 right-0 mt-1 max-h-64 overflow-auto rounded-md border border-zinc-200 bg-white shadow-lg"
        >
          {suggestions.length === 0 && !isLoading && (
            <li className="px-3 py-2 text-sm text-zinc-500">
              Aucune école trouvée — votre saisie sera enregistrée telle quelle.
            </li>
          )}
          {suggestions.map((school, index) => {
            const isHighlighted = index === highlightedIndex;
            return (
              <li
                key={school.id}
                role="option"
                aria-selected={isHighlighted}
                onMouseEnter={() => setHighlightedIndex(index)}
                onMouseDown={(event) => {
                  // mousedown plutôt que click pour devancer le blur de l'input
                  event.preventDefault();
                  handleSelect(school);
                }}
                className={`cursor-pointer px-3 py-2 text-sm ${
                  isHighlighted ? 'bg-zinc-900 text-white' : 'text-zinc-900 hover:bg-zinc-50'
                }`}
              >
                <div className="font-medium">{school.name}</div>
                {(school.city || school.postal_code) && (
                  <div className={`text-xs ${isHighlighted ? 'text-zinc-300' : 'text-zinc-500'}`}>
                    {[school.postal_code, school.city].filter(Boolean).join(' ')}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
